import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import EmailSignup from './EmailSignup';

const Footer = () => {
  const footerSections = [
    {
      title: "Programs",
      links: [
        { name: "Spark Moments™", href: "/spark-moments", isRoute: true },
        { name: "Leadership Accelerator", href: "/crucible", isRoute: true },
        { name: "LIT Expeditions™", href: "/expeditions", isRoute: true },
        { name: "Custom Programs", href: "/contact?inquiry=custom", isRoute: true }
      ]
    },
    {
      title: "Connect",
      links: [
        { name: "About", href: "/about", isRoute: true },
        { name: "Speaking", href: "/contact", isRoute: true },
        { name: "Consulting", href: "/contact", isRoute: true },
        { name: "Partnerships", href: "/contact", isRoute: true },
        { name: "Contact", href: "/contact", isRoute: true }
      ]
    }
  ];

  const socialLinks = [
    { icon: Twitter, href: "https://twitter.com/EmberLeadership", label: "Follow EMBER on Twitter" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/ember-impact-ventures", label: "Connect with EMBER on LinkedIn" },
    { icon: Instagram, href: "https://instagram.com/emberleadership", label: "Follow EMBER on Instagram" }
  ];

  return (
    <footer role="contentinfo" aria-label="Site footer" className="bg-neutral-charcoal text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-64 h-64 bg-ember-fire rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-ember-depth rounded-full blur-2xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Brand Section - Logo + Text */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Link to="/" className="inline-flex items-center mb-6 group">
                <motion.img 
                  src="https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
                  alt="EMBER Logo"
                  className="w-12 h-12 mr-4 group-hover:scale-105 transition-transform duration-300"
                  whileHover={{ scale: 1.05 }}
                />
                <motion.span 
                  className="text-4xl font-bold hover:text-ember-spark transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  EMBER
                </motion.span>
              </Link>
              <p className="text-lg text-neutral-light mb-6 leading-relaxed">
                Ignite Your Leadership. Transform Your World.
              </p>
              <p className="text-neutral-light mb-8 leading-relaxed">
                We believe that true leadership begins not with strategy or systems, 
                but with the courage to face yourself fully and tend the flame within.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center space-x-3 text-neutral-light">
                  <Mail className="w-4 h-4 text-ember-spark" />
                  <span>hello@emberimpact.com</span>
                </div>
                <div className="flex items-center space-x-3 text-neutral-light">
                  <Phone className="w-4 h-4 text-ember-spark" />
                  <span>415-347-6475</span>
                </div>
              </div>

              {/* Social Links */}
              <nav aria-label="Social media links" className="flex space-x-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-ember-fire transition-colors"
                      whileHover={{ scale: 1.1, y: -2 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <IconComponent className="w-5 h-5" aria-hidden="true" />
                    </motion.a>
                  );
                })}
              </nav>
            </motion.div>

            {/* Navigation Sections */}
            {footerSections.map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="font-bold text-xl mb-6 text-white">{section.title}</h4>
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <motion.li 
                      key={link.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: linkIndex * 0.05 }}
                      viewport={{ once: true }}
                    >
                      {link.isRoute ? (
                        <Link
                          to={link.href}
                          className="text-neutral-light hover:text-ember-spark transition-colors inline-block"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-neutral-light hover:text-ember-spark transition-colors inline-block"
                        >
                          {link.name}
                        </a>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Newsletter Section */}
        <motion.div 
          className="py-12 border-t border-neutral-dark"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4 text-white">Join the EMBER Community</h3>
            <p className="text-neutral-light mb-8">
              Get weekly insights, leadership practices, and exclusive access to our community of transformational leaders.
            </p>
            
            {/* Replace manual form with EmailSignup component */}
            <EmailSignup 
              formId="8170380"
              title=""
              subtitle=""
              buttonText="Subscribe"
              placeholder="Enter your email"
              variant="inline"
              className="justify-center"
            />
          </div>
        </motion.div>

        {/* Bottom Footer */}
        <div className="py-8 border-t border-neutral-dark">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <motion.div 
              className="text-neutral-light text-center md:text-left mb-4 md:mb-0"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p>&copy; 2025 Ember Impact Ventures LLC. All rights reserved.</p>
              <p className="text-sm mt-1">Built with purpose, powered by connection.</p>
            </motion.div>
            
            <motion.nav 
              aria-label="Legal links"
              className="flex space-x-6 text-sm text-neutral-light"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Link to="/privacy" className="hover:text-ember-spark transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-ember-spark transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="hover:text-ember-spark transition-colors">Cookie Policy</Link>
            </motion.nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;